import { Component, computed, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UserApi } from "@front/m19-api-client";
import { AuthService, OrganizationUsersService } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonComponent,
  IInputComponent,
  MODAL_DATA,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { catchAjaxError } from "@front/m19-utils";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { iif, Observable, of } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap } from "rxjs/operators";

export interface AddAdminModalData {
  organizationId: number;
  organizationName: string;
}

@Component({
  templateUrl: "./add-admin-modal.component.html",
  standalone: true,
  imports: [TranslocoDirective, ReactiveFormsModule, IInputComponent, IButtonComponent, IAlertComponent],
})
export class AddAdminModalComponent {
  readonly modalData = inject(MODAL_DATA) as AddAdminModalData;
  readonly modalRef = inject(ModalRef);
  readonly destroyRef = inject(DestroyRef);

  constructor(
    private toasterService: ToasterService,
    private organizationUsersService: OrganizationUsersService,
    private translocoService: TranslocoService,
    private userApi: UserApi,
    private authService: AuthService,
  ) {}

  readonly emailForm = new FormGroup({
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
  });

  get email(): AbstractControl {
    return this.emailForm.controls.email;
  }

  private readonly emailValue$: Observable<string | null> = this.email.valueChanges.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    filter((_) => this.emailForm.controls.email.valid && this.email.value !== null),
  );

  private readonly loggedUser = toSignal(this.authService.loggedUser$);

  checkEmailLoading = signal(false);
  addAdminLoading = signal(false);
  emailValue = toSignal<string | null>(this.emailValue$.pipe(tap(() => this.checkEmailLoading.set(true))));

  isEmailRegistered = toSignal(
    this.emailValue$.pipe(
      switchMap((email) => {
        if (!email) return of(true);
        return this.userApi.doesUserExist({ email }).pipe(
          map((_) => true),
          catchError((_) => of(false)),
          finalize(() => this.checkEmailLoading.set(false)),
        );
      }),
    ),
  );

  buttonLabel = computed(() => {
    if (this.isEmailRegistered() === false) {
      return this.translocoService.translate("user-management.send_invite");
    }
    return this.translocoService.translate("common.add");
  });

  addAdmin(): void {
    this.addAdminLoading.set(true);

    const initCall = iif(
      () => !this.isEmailRegistered(),
      this.userApi.registerUser({
        userBase: {
          email: this.email.value,
          invitedBy: this.loggedUser()?.userId,
        },
      }),
      of({}),
    );

    initCall
      .pipe(
        switchMap((_) => this.organizationUsersService.addAdmin(this.modalData.organizationId, this.email.value)),
        switchMap((_) =>
          iif(
            () => !this.isEmailRegistered(),
            this.userApi
              .sendUserInvitation({
                email: this.email.value,
                domain: window.location.hostname,
                organizationId: this.modalData.organizationId,
              })
              .pipe(catchAjaxError()),
            of({}),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("user-management.success_invite_sent", [this.email.value]),
          );
          this.modalRef.close();
        },
        error: (error) => {
          this.toasterService.error(error, "Admin creation error");
          this.addAdminLoading.set(false);
        },
      });
  }
}
